import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import { isNullOrEmpty } from '@/utils/vuex';
import { replaceSpecialChars } from '@/utils/vuex';
import ApiService from '@/services/apiService';
const BRIDGE_TYPES_WITH_CONFIG = ['bridge-IEC-60870-5-104-push'];
const NON_EDITABLE_KEYS = ['_raw', '_health', '_parsed'];
export default {
  name: 'CoreDtwinStateEdit',
  props: {
    // the current edited state
    value: {
      type: Object,
      required: false,
      default: () => ({})
    },
    twinType: {
      type: String,
      required: false,
      default: null
    },
    // is this an edit, create, or readonly?
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    },
    tenantId: {
      type: Number,
      required: true
    },
    // the original twin(/virtual-device)-model
    // to be used by some components for additional requesting data
    twinModel: {
      type: Object,
      required: false,
      default: () => ({})
    },
    dataSources: {
      type: Array,
      required: true
    },
    bridgeConfigs: {
      type: Array,
      required: false,
      default: () => []
    },
    otherStates: {
      type: Array,
      required: false,
      default: () => []
    },
    deviceTemplate: {
      type: Object,
      required: false,
      default: () => ({})
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    },
    stateDefinition: {
      type: Array,
      required: false,
      default: () => []
    },
    keyIsEditable: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data: function () {
    return {
      lclModel: {},
      manualTitleEdit: false,
      manualKeyEdit: false,
      manualUnitEdit: false,
      aggregatedStateValue: '',
      innerFormIsValid: false,
      wholeFormIsValid: false,
      requiredRule: [v => !!v || this.$i18n_t('Common.fieldRequired')],
      openApplyConfigModal: false,
      connectors: [],
      linkedDeviceError: null
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    shouldRenderOutboundConfigPanel() {
      if (this.templateContext) {
        return false;
      }
      const hasOutboundConfig = get(this.lclModel, 'properties.outboundConfiguration');
      return hasOutboundConfig || this.connectorsWithStateSetting.length > 0;
    },
    isCreatedByTemplate() {
      return !!this.deviceTemplate;
    },
    isGeoState() {
      return this.isSingleDimensionGeoState || get(this.lclModel, 'properties.valueType') === 'json';
    },
    isSingleDimensionGeoState() {
      return (
        // NOTE: only show single-dimension state selection for existing states now
        // new states should preferrably use an aggregation/json link of geo-coordinates
        this.lclModel.id && /^(lat)|(lon)|(lng).*?/gi.exec(this.lclModel.key) && get(this.lclModel, 'properties.valueType') === 'number'
      );
    },
    keyRules() {
      return [v => !!v && this.isStateKeyUnique(v) || this.$i18n_t('DigitalTwin.DigitalTwin.states.keyMustBeUnique'),
      // eslint-disable-next-line no-useless-escape
      v => !!v && /^([A-Za-z0-9._-])+?$/gi.exec(v) !== null || this.$i18n_t('DigitalTwin.DigitalTwin.states.keyText')];
    },
    allowsVisualization() {
      return !this.lclModel.properties.linkGeoCoordinate && (this.lclModel.properties.valueType === 'number' || this.lclModel.properties.valueType === 'boolean' || this.lclModel.properties.valueType === 'string' || this.lclModel.properties.valueType === 'json');
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    copyConfig() {
      return this.$store.state.app.dtwinStateConfigCopy;
    },
    connectorsWithStateSetting() {
      return this.connectors.filter(b => BRIDGE_TYPES_WITH_CONFIG.includes(b.type));
    },
    showNotChangeableKeyPopup() {
      return this.templateContext && this.mode !== 'create' && !this.keyIsEditable;
    },
    isKeyFieldDisabled() {
      return this.mode === 'readonly' || this.showNotChangeableKeyPopup || this.lclModel.id && this.twinType === 'virtualDevice' && NON_EDITABLE_KEYS.includes(this.lclModel.key);
    },
    stateEditable() {
      return !NON_EDITABLE_KEYS.includes(this.lclModel.key) && !!this.deviceTemplate;
    }
  },
  watch: {
    value() {
      this.lclModel = this.cloneLclModel(this.value);
      this.linkedDeviceError = null;
    },
    isGeoState() {
      if (this.isGeoState) {
        if (/^(lat).*?/gi.exec(this.lclModel.key) !== null) {
          this.lclModel.properties.geoCoordinateKey = 'lat';
        } else if (/^(lon)|(lng).*?/gi.exec(this.lclModel.key) !== null) {
          this.lclModel.properties.geoCoordinateKey = 'lon';
        }
      }
    }
  },
  created() {
    this.lclModel = this.cloneLclModel(this.value);
    if (this.mode === 'create') {
      this.lclModel = {
        value: {},
        properties: {
          valueType: 'number'
        }
      };
    }
    if (!get(this.lclModel, 'properties.binding')) {
      this.lclModel.properties = Object.assign({}, this.lclModel.properties, {
        binding: {
          type: 'none',
          options: {
            aggregateStateBindingIds: []
          }
        }
      });
    }
    if (!get(this.lclModel, 'properties.outboundConfiguration') || get(this.lclModel, 'properties.outboundConfiguration').length === 0) {
      set(this.lclModel, 'properties.outboundConfiguration', [{}]);
    }
    this.manualTitleEdit = !isNullOrEmpty(this.lclModel.properties.title);
    this.manualKeyEdit = !isNullOrEmpty(this.lclModel.key);
    this.manualUnitEdit = !isNullOrEmpty(this.lclModel.properties.unit);
    this.changedBindingType();
    this.resetVisualProperties();
  },
  mounted() {
    ApiService.getBridgesForTenant(this.tenantId).then(bridges => {
      this.connectors = bridges;
    }).catch(err => {
      console.error(err);
    });
  },
  methods: {
    cloneLclModel(model) {
      const cloned = cloneDeep(model);

      /*
      In commit 7477cfe4 the visual type was changed from donut to doughnut.
      https://gitlab.com/digimondo-code/niotix/-/commit/7477cfe4ed65b50eb898ece9717eb23138e8ae50#99df089ce2b08b0093f64195288000a23c109f97_644_644
       With this change, older persisted states with a visual type of donut were not rendered as there was no 'donut' chart type anymore.
      This is a fix to ensure that older persisted states with a visual type of donut are rendered as doughnut.
      */

      if (get(cloned, 'properties.visual.type') === 'donut') {
        set(cloned, 'properties.visual.type', 'doughnut');
      }
      return cloned;
    },
    updateLclModel(updatedModel) {
      this.lclModel = this.cloneLclModel(updatedModel);
    },
    onTransformerUpdate(newTransformer) {
      this.$emit('input', newTransformer);
    },
    resetVisualProperties() {
      if (this.mode === 'create') {
        this.lclModel.properties.visual = {
          options: {}
        };
      }
    },
    copyStateConfig() {
      this.$store.state.app.dtwinStateConfigCopy = cloneDeep(this.lclModel);
    },
    doApplyConfig(paths) {
      paths.forEach(path => {
        set(this.lclModel, path, get(this.copyConfig, path, get(this.lclModel, path)));
      });
      // set the manual edits to true, so that the title/key/unit are not automatically overwritten
      this.manualUnitEdit = true;
      this.manualKeyEdit = true;
      this.manualTitleEdit = true;
      this.lclModel = this.cloneLclModel(this.lclModel);
      this.openApplyConfigModal = false;
    },
    isStateKeyUnique(v) {
      return !this.otherStates.find(s => {
        if (this.templateContext) {
          return s.key === v && isNaN(this.lclModel.indexOfEditedElement);
        }
        s.key === v && s.id !== this.lclModel.id;
      });
    },
    setIcon(icon) {
      this.lclModel.properties.faIcon = icon;
    },
    changedBindingType() {
      // clear linked-device error when binding changes
      this.linkedDeviceError = null;
      if (
      // only update the valueType automatically if the binding-type was changed JUST NOW
      // it changedBindingType is also called on create/init
      get(this.value, 'properties.valueType') === undefined || get(this.value, 'properties.binding.type') !== this.lclModel.properties.binding.type) {
        if (this.lclModel.properties.binding.type === 'random') {
          this.lclModel.properties.valueType = 'number';
        } else if (['aggregate', 'iothub-aggregate', 'virtual-device-aggregate'].indexOf(this.lclModel.properties.binding.type) >= 0) {
          this.lclModel.properties.valueType = 'json';
        }
      }
      this.validateForm();
    },
    validateForm() {
      this.$nextTick(() => {
        this.innerFormIsValid = this.$refs.form.validate();
        const sourceEditRefs = get(this, '$refs.sourceEdit.$refs') || {};
        const bindingType = get(this.lclModel, 'properties.binding.type');

        // if bindingType is not a string the form is immediately invalid
        if (typeof bindingType !== 'string') {
          this.wholeFormIsValid = false;
          return;
        }
        if (bindingType === 'bridge') {
          this.wholeFormIsValid = !isNullOrEmpty(sourceEditRefs.bridgeBindingEdit) && sourceEditRefs.bridgeBindingEdit.formIsValid;
        } else if (bindingType.startsWith('iothub')) {
          this.wholeFormIsValid = !isNullOrEmpty(sourceEditRefs.iothubBindingEdit) && sourceEditRefs.iothubBindingEdit.formIsValid;
        } else if (bindingType.startsWith('virtual-device')) {
          this.wholeFormIsValid = !isNullOrEmpty(sourceEditRefs.virtualDeviceBindingEdit) && sourceEditRefs.virtualDeviceBindingEdit.formIsValid;
        } else if (bindingType === 'aggregate') {
          this.wholeFormIsValid = !isNullOrEmpty(sourceEditRefs.aggregateBindingEdit) && sourceEditRefs.aggregateBindingEdit.aggregateStateLoops === false;
        } else if (this.lclModel.properties.visual.type === 'lineChart') {
          this.wholeFormIsValid = (!this.lclModel.properties.visual.options.minYAxis || !isNaN(parseFloat(this.lclModel.properties.visual.options.minYAxis))) && (!this.lclModel.properties.visual.options.maxYAxis || !isNaN(parseFloat(this.lclModel.properties.visual.options.maxYAxis)));
        } else {
          this.wholeFormIsValid = true;
        }
        const stateVisualComponentVisible = !isNullOrEmpty(this.$refs.stateVisualComp);
        this.wholeFormIsValid = this.wholeFormIsValid && (!this.$refs.stateTransformerComp || this.$refs.stateTransformerComp.formIsValid);
        this.wholeFormIsValid = this.wholeFormIsValid && this.innerFormIsValid && stateVisualComponentVisible ? this.$refs.stateVisualComp.formIsValid : true;
      });
    },
    titleEntered() {
      this.manualTitleEdit = true;
      if (!this.manualKeyEdit) {
        this.setKeyFromTitle(this.lclModel.properties.title);
      }
      this.validateForm();
    },
    setKeyFromTitle(title) {
      this.lclModel.key = replaceSpecialChars(title);
    },
    keyEntered() {
      this.manualKeyEdit = true;
      this.validateForm();
    },
    // ----
    // NIOTA OR IOTHUB
    // ----

    noPermissionError(error) {
      this.close(error || 'NOT AUTHORIZED FOR THIS STATE');
    },
    deviceNotFoundError(message) {
      this.linkedDeviceError = message || this.$i18n_t('DigitalTwin.DigitalTwin.states.linkedDeviceNotFound');
    },
    bridgeBindingChanged(val) {
      const variable = val.variable;
      const valueType = val.valueType;
      const value = val.value;
      if (!!variable) {
        // clear linked-device error when a new variable is selected
        this.linkedDeviceError = null;
        if (!this.manualTitleEdit || isNullOrEmpty(this.lclModel.properties.title)) {
          this.lclModel.properties.title = variable;
        }
        if (isNullOrEmpty(this.lclModel.key)) {
          this.setKeyFromTitle(variable);
        }
        if (!!valueType && (!this.manualUnitEdit || isNullOrEmpty(this.lclModel.properties.unit))) {
          this.lclModel.properties.unit = valueType;
        }
        if (!!value) {
          this.lclModel.value.value = value;
        }
        this.lclModel = this.cloneLclModel(this.lclModel);
      }
      this.validateForm();
    },
    // -----
    // AGGREGATE
    // -----

    aggregatedStateValueChanged(aggregatedStateValue) {
      this.aggregatedStateValue = aggregatedStateValue;
    },
    handleSubmit() {
      this.$refs.stateValueComp.saveState();
      const sourceEditRefs = get(this, '$refs.sourceEdit.$refs') || {};
      if (this.lclModel.properties.binding.type === 'bridge') {
        if (sourceEditRefs.bridgeBindingEdit && sourceEditRefs.bridgeBindingEdit.saveState) {
          sourceEditRefs.bridgeBindingEdit.saveState();
        }
      } else if (this.lclModel.properties.binding.type.startsWith('iothub')) {
        sourceEditRefs.iothubBindingEdit.saveState();
      } else if (this.lclModel.properties.binding.type.startsWith('virtual-device')) {
        sourceEditRefs.virtualDeviceBindingEdit.saveState();
      } else if (this.lclModel.properties.binding.type === 'aggregate') {
        sourceEditRefs.aggregateBindingEdit.saveState();
      }
      if (this.$refs.stateTransformerComp) {
        this.$refs.stateTransformerComp.saveState();
      }
      const cloneData = cloneDeep(this.lclModel);
      cloneData.properties.outboundConfiguration = cloneData.properties.outboundConfiguration.filter(config => config.connectorId);
      this.$emit('save', cloneData);
    },
    close(errorMessage) {
      this.$emit('close', errorMessage);
    },
    onTransformerPanelChange() {
      this.$nextTick(() => {
        const stateTransformerRefreshForm = get(this, '$refs.stateTransformerComp.refreshForm');
        if (typeof stateTransformerRefreshForm === 'function') {
          stateTransformerRefreshForm();
        }
      });
    }
  }
};